import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import { EyeIcon, ControlsIcon, AIMagicStarIcon } from 'shared/components/Icons';
import LikieeAISummary from './LikieeAISummary';
import AISummaryButton from 'shared/components/Credits/AISummaryButton';
import AICompareButton from 'shared/components/AICompare/AICompareButton';

const ProductInfoTabs = ({
  sourceName,
  favIcon,
  product,
  description,
  properties,
  isLoadingProduct,
  aiSummary,
  reviewsAggregation,
  onAIButtonSuccess,
  onAICompareClick,
}) => {
  const { t } = useTranslation();

  // Check if AI data is available
  const hasAIData = aiSummary || reviewsAggregation;

  // Initialize with AI Insights tab if data is available, otherwise overview
  const [activeTab, setActiveTab] = useState(hasAIData ? 'ai-insights' : 'overview');
  const [descToggle, setDescToggle] = useState(false);
  const [hasInitializedAITab, setHasInitializedAITab] = useState(hasAIData);

  // Update active tab when AI data becomes available for the first time only
  useEffect(() => {
    if (hasAIData && !hasInitializedAITab) {
      setActiveTab('ai-insights');
      setHasInitializedAITab(true);
    }
  }, [hasAIData, hasInitializedAITab]);

  // Show skeleton loading during server fetch or data extraction/enhancement
  const isLoading = isLoadingProduct || (product && (product.status === 'EXTRACTING' || product.isEnhancingInProgress));

  if (isLoading) {
    return (
      <div className='product-info-tabs'>
        <SkeletonLoader type='basic' count={6} />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t('tab_overview', 'Overview'), icon: EyeIcon },
    { id: 'features', label: t('tab_features', 'Features'), icon: ControlsIcon },
    {
      id: 'ai-insights',
      label: hasAIData ? t('tab_ai_insights', 'AI Insights') : t('tab_generate_ai_insights', 'Generate AI Insights'),
      icon: AIMagicStarIcon,
      isGenerate: !hasAIData,
    },
  ];
  const renderOverview = () => (
    <div className='tab-content overview-content'>
      <div className='info-cards'>
        {/* Description Card */}
        <div className='info-card description-card'>
          <div className='card-header'>
            <h5>{t('title_about', 'About this product')}</h5>
          </div>
          <div className='card-body'>
            <div className='description-text'>
              <p className={`description-paragraph ${descToggle ? 'expanded' : 'collapsed'}`}>
                {description || t('no_description', 'No description available for this product.')}
              </p>
              {description && description.length > 200 && (
                <button className='toggle-description-btn' onClick={() => setDescToggle(!descToggle)}>
                  {descToggle ? t('text_read_less', 'Read Less') : t('text_read_more', 'Read More')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Source Info Card */}
        <div className='info-card source-card'>
          <div className='card-header'>
            <h5>{t('source_information', 'Source Information')}</h5>
          </div>
          <div className='card-body'>
            <div className='source-info'>
              <img width={24} height={24} className='source-icon' src={favIcon} alt='logo' />
              <div className='source-details'>
                <span className='source-name'>{sourceName}</span>
                <span className='source-date'>{moment(product.createdAt).format('DD.MM.YY')}</span>
              </div>
            </div>
            <div className='date-added'>
              <span className='label'>{t('date_added', 'Date added')}:</span>
              <span className='value'>{moment(product.createdAt).format('DD MMMM YYYY')}</span>
            </div>
            {product.item?.brand && (
              <div className='brand-info'>
                <span className='label'>{t('label_brand', 'Brand')}:</span>
                <span className='value'>{product.item.brand}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  const renderFeatures = () => (
    <div className='tab-content features-content'>
      <div className='info-card features-card'>
        <div className='card-header'>
          <h5>{t('title_features', 'Features & Specifications')}</h5>
          <span className='feature-count'>
            {properties.length + (product.features?.length || 0)} {t('items', 'items')}
          </span>
        </div>
        <div className='card-body'>
          {properties.length > 0 && (
            <div className='features-table-container'>
              <div className='features-table'>
                {properties.map((item, i) => (
                  <div className='feature-row' key={i}>
                    <div className='feature-name'>{item.name}</div>
                    <div className='feature-value'>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.features?.length > 0 && (
            <div className='feature-tags'>
              <h6>{t('additional_features', 'Additional Features')}</h6>
              <div className='tags-container'>
                {product.features.map((item, i) => (
                  <span className='feature-tag' key={i}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!properties.length && !product.features?.length && (
            <div className='no-features'>
              <p>{t('no_features', 'No features information available for this product.')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAIInsights = () => (
    <div className='tab-content ai-insights-content'>
      <div className='ai-insights-container'>
        {aiSummary || reviewsAggregation ? (
          <LikieeAISummary
            aiSummary={aiSummary}
            reviewsAggregation={reviewsAggregation}
            productName={product?.name}
            isLoading={!product}
            likieeId={product?.id}
            showAIButton={true}
            onAIButtonSuccess={onAIButtonSuccess}
          />
        ) : (
          <div className='info-card generate-insights-card'>
            <div className='card-header'>
              <h5>{t('ai_generate_cta_title', 'Generate AI Insights')}</h5>
            </div>
            <div className='card-body'>
              <div className='generate-content'>
                <p className='generate-description'>
                  {t(
                    'ai_insights_generate_description',
                    'Get detailed AI-powered analysis and insights for this product.',
                  )}
                </p>
                {product?.id && (
                  <AISummaryButton
                    likieeId={product.id}
                    className='generate-ai-button-tab'
                    onSuccess={onAIButtonSuccess}
                    showCreditCost={true}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return (
    <div className='product-info-tabs'>
      {/* Tab Navigation */}
      <div className='tabs-nav'>
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.isGenerate ? 'generate-tab' : ''}`}
              data-tab-id={tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className='tab-icon'>
                <IconComponent />
              </span>
              <span className='tab-label'>{tab.label}</span>
            </button>
          );
        })}
        {/* AI Compare Button */}
        {onAICompareClick && (
          <div className='ai-compare-button-container'>
            <AICompareButton onClick={onAICompareClick} />
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div className='tabs-content'>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'features' && renderFeatures()}
        {activeTab === 'ai-insights' && renderAIInsights()}
      </div>
    </div>
  );
};
export default ProductInfoTabs;
